/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import * as React from "react";
import { Trash2, Loader2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { InputField } from "../../../components/Shared/Radix/Inputs/InputField";
import { Label } from "../../../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../../../components/ui/radio-group";
import { IDuCellInfo } from "../../../store/api/devices/types";
import StatusIcon from "../../../components/Shared/Status/StatusIcon";
import { IOption } from "../../../components/Shared/Filter/types";
import ConfirmDialog from "../../../components/Shared/Dialogs/ConfirmDialog";
import { constStr } from "../../../globals/constants";
import { DIALOGOPENFOR } from "../../../globals/globals";
import { setConfirmDialog } from "../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../store/hooks";
import { confirmDialogSelector } from "../../../store/helpers/helperSelectors";
import { useSelector } from "react-redux";
import { selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { IDusNodeDataGridRow, IFullDeviceDetails, INrCell, IRadios } from "../../../store/api/deviceConfig/types";
import { setOpenDuDrawer } from "../../../store/api/devices/reducers";
import { selectProfilePermissionsSelector } from "../../../store/api/auth/selectors";
import ProfilePermissions from "../../../utils/ProfilePermissions";

interface IPayload {
  [key: string]: string;
}
interface IRadioCellInfo {
  selectedRadioNode?: IDusNodeDataGridRow;
  cellFormData?: IDuCellInfo;
  isCreateRadioCell?: boolean;
  cellType?: string;
  status?: string;
  zones?: IOption[];
  zoneOption?: IOption[];
  selectedBand?: string;
  isAddEditType?: string;
  setCellFormData?: (formData: IDuCellInfo, type: string) => void;
  handleSelectBand?: (val: string, type: string) => void;
  handleUpdateCellInfoOption?: (args: boolean, type: string) => void;
  handleSetSelectedZones?: (newValue: any | null, type: string) => void;
  handleSubmit?: (type: string) => void;
  lteCellList?: string[];
  onBlurHandler?: (event: any) => void;
  errMsg?: IPayload;
  configStatus?: boolean;
  radios?: IRadios[];
  bandData?: string;
  nrCellListData?: INrCell[];
}

const RadioCellInformation: React.FC<IRadioCellInfo> = ({
  selectedRadioNode,
  cellFormData,
  isCreateRadioCell = false,
  cellType = "",
  status,
  zones,
  zoneOption,
  selectedBand = "",
  isAddEditType = "add",
  setCellFormData,
  handleUpdateCellInfoOption,
  handleSelectBand,
  handleSetSelectedZones,
  handleSubmit,
  lteCellList = [],
  onBlurHandler,
  errMsg,
  configStatus,
  radios = [],
  bandData = "",
  nrCellListData = [],
}) => {
  const dispatch = useStoreDispatch();
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const selectedDevice: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);

  const handleChange = (name: string, value: string) => {
    let newformData = { ...cellFormData, [name]: value };
    if (setCellFormData) {
      setCellFormData(newformData, cellType);
    }
  };

  const handleDeleteCell = () => {
    if (handleSubmit) {
      handleSubmit("delete");
    }
  };

  const handleDeleteCellModal = () => {
    dispatch(setConfirmDialog(DIALOGOPENFOR.CELLDELETE));
  };

  /*
  const autocompleteSelectHandler = (event: any, newValue: any | null) => {
    if (handleSetSelectedZones) {
      handleSetSelectedZones(newValue, cellType);
    }
  };
*/

  const handleSelect = (value: string) => {
    if (handleSelectBand) {
      handleSelectBand(value, cellType);
    }
  };

  const handleSubmitCellConfig = () => {
    if (handleSubmit) {
      handleSubmit(isAddEditType);
    }
  };

  const handleIsCreateCell = () => {
    let isDisabled = false;
    if (cellType === "radio1") {
      if (Array.isArray(nrCellListData) && nrCellListData?.length > 0) {
        let cellObj: any = {};
        cellObj = nrCellListData?.find((el: INrCell) => Number(el?.radioNode) === selectedRadioNode?.id);
        if (!!cellObj?.index) {
          isDisabled = true;
        }
      }
    }
    return isDisabled;
  };

  return (
    <>
      {openConfirmDialog === DIALOGOPENFOR.CELLDELETE && (
        <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={handleDeleteCell}>
          Are you sure you want to delete <strong>{cellFormData?.cellName || "Unknown"}</strong>?
        </ConfirmDialog>
      )}
      <div className="pl-4">
        <Label className="text-sm font-medium min-w-[205px]">Band</Label>
        <RadioGroup
          value={selectedBand || ""}
          onValueChange={handleSelect}
          className="flex flex-row gap-4 mt-1"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="nr-band-77" id="nr-band-77" />
            <Label htmlFor="nr-band-77" className="cursor-pointer">NR-BAND-77</Label>
          </div>
        </RadioGroup>
      </div>
      <div className="w-full pl-2 flex justify-between items-center">
        <div>
          <h5 className="text-xl font-semibold text-foreground mb-2">Cell Information</h5>
          <StatusIcon status={status || ""} />
        </div>
        <div>
          {permissions?.hasOpCodeGroup("manage_topology") && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="border-0"
              title="Delete Cell"
              disabled={configStatus}
              onClick={handleDeleteCellModal}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      </div>
      <InputField
        name="cellId"
        label="Cell ID"
        type="number"
        disabled={configStatus || handleIsCreateCell()}
        value={cellFormData?.cellId ?? ""}
        onChange={handleChange}
        onBlur={onBlurHandler}
        min={1}
      />
      <InputField
        name="cellName"
        label="Cell Name"
        disabled={configStatus}
        value={cellFormData?.cellName ?? ""}
        onChange={handleChange}
        onBlur={onBlurHandler}
      />
      <InputField
        name="cellDescription"
        label="Cell Description"
        disabled={configStatus}
        value={cellFormData?.cellDescription ?? ""}
        onChange={handleChange}
        onBlur={onBlurHandler}
      />
      <InputField
        name="cellPci"
        label="Cell PCI"
        type="number"
        disabled={configStatus}
        value={cellFormData?.cellPci ?? ""}
        onChange={handleChange}
        onBlur={onBlurHandler}
        min={1}
      />
      {parseFloat(selectedDevice?.swVersion) >= 23.4 ? (
        <InputField
          name="txPower"
          label="TX power percentage"
          type="number"
          step="any"
          disabled={configStatus}
          value={cellFormData?.txPower ?? ""}
          onChange={handleChange}
          onBlur={onBlurHandler}
        />
      ) : (
        <InputField
          name="pbchPower"
          label="PBCH reference signal power"
          type="number"
          step="any"
          disabled={configStatus}
          value={cellFormData?.pbchPower ?? ""}
          onChange={handleChange}
          onBlur={onBlurHandler}
        />
      )}
      <InputField
        name="commonCellId"
        label="Common Cell ID"
        type="number"
        disabled={configStatus || handleIsCreateCell()}
        value={cellFormData?.commonCellId ?? ""}
        onChange={handleChange}
        onBlur={onBlurHandler}
      />
      {/* <div className="flex justify-end gap-2">
        <Button variant="outline" disabled={configStatus} onClick={() => dispatch(setOpenDuDrawer(false))}>
          Cancel
        </Button>
        {permissions?.hasOpCodeGroup("manage_topology") && (
          <Button
            disabled={configStatus || selectedDevice?.status === 19}
            onClick={handleSubmitCellConfig}
          >
            {configStatus && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        )}
      </div> */}
    </>
  );
};

export default RadioCellInformation;
